import { marked } from 'marked'
import DOMPurify from 'dompurify'

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'span'
]
const ALLOWED_ATTR = ['href', 'title', 'target', 'rel']

const HIGHLIGHT_CLASS = 'rounded bg-catering-honey-100 px-1 font-medium text-catering-ink-800'
const SKIP_TAGS = new Set(['A', 'CODE', 'PRE'])

const WEEKDAY = '(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*'
const MONTH = '(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*'
const CLOCK = '\\d{1,2}:\\d{2}'
const MERIDIEM = '\\s?[APap][Mm]'
const DASH = '\\s?[–—-]\\s?'

const DATE_TIME_SOURCE = [
  `${CLOCK}${MERIDIEM}${DASH}${CLOCK}${MERIDIEM}`,
  `(?<!\\d)\\d{1,2}\\s+${MONTH}\\s+\\d{4}(?!\\d)`,
  `${WEEKDAY},?\\s+(?:${MONTH}\\s+\\d{1,2}(?!\\d)|(?<!\\d)\\d{1,2}\\s+${MONTH})(?:,?\\s+\\d{4})?`,
  `${MONTH}\\s+\\d{1,2}(?!\\d)`,
  `${CLOCK}${MERIDIEM}(?:\\s?UTC)?`
].join('|')

let DATE_TIME_RE
try {
  DATE_TIME_RE = new RegExp(`(${DATE_TIME_SOURCE})`, 'g')
} catch (e) {
  DATE_TIME_RE = null
}

function forceBlankLinkRel(node) {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }
}

function isSkipped(node, root) {
  let parent = node.parentNode
  while (parent && parent !== root) {
    if (SKIP_TAGS.has(parent.nodeName)) return true
    parent = parent.parentNode
  }
  return false
}

function collectTextNodes(doc, root) {
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node => (isSkipped(node, root) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT)
  })
  const nodes = []
  let current = walker.nextNode()
  while (current) {
    nodes.push(current)
    current = walker.nextNode()
  }
  return nodes
}

function wrapTextNode(doc, textNode) {
  const text = textNode.nodeValue
  DATE_TIME_RE.lastIndex = 0
  let fragment = null
  let lastIndex = 0
  let match = DATE_TIME_RE.exec(text)
  while (match) {
    if (match[0].length === 0) {
      DATE_TIME_RE.lastIndex += 1
      match = DATE_TIME_RE.exec(text)
      continue
    }
    if (!fragment) fragment = doc.createDocumentFragment()
    const start = match.index
    if (start > lastIndex) fragment.appendChild(doc.createTextNode(text.slice(lastIndex, start)))
    const span = doc.createElement('span')
    span.className = HIGHLIGHT_CLASS
    span.textContent = match[0]
    fragment.appendChild(span)
    lastIndex = start + match[0].length
    match = DATE_TIME_RE.exec(text)
  }
  if (fragment) {
    if (lastIndex < text.length) fragment.appendChild(doc.createTextNode(text.slice(lastIndex)))
    textNode.parentNode.replaceChild(fragment, textNode)
  }
}

function highlightDateTimes(clean) {
  if (!clean || !DATE_TIME_RE || typeof DOMParser === 'undefined') return clean
  const doc = new DOMParser().parseFromString(clean, 'text/html')
  const body = doc.body
  if (!body) return clean
  for (const textNode of collectTextNodes(doc, body)) wrapTextNode(doc, textNode)
  return body.innerHTML
}

export function renderBotMarkdown(text) {
  const source = text == null ? '' : String(text)
  if (!source.trim()) return ''
  const rawHtml = marked.parse(source, { gfm: true, breaks: true })
  DOMPurify.addHook('afterSanitizeAttributes', forceBlankLinkRel)
  const clean = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS: ['script', 'style', 'iframe', 'img', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'style'],
    ALLOW_DATA_ATTR: false
  })
  DOMPurify.removeHook('afterSanitizeAttributes')
  return highlightDateTimes(clean)
}
